import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Booking, PartnerPricing } from '@/types/partner';
import { BookingPayment, BookingVerificationResponse } from '@/types/booking';
import { toast } from 'sonner';
import { useCentralizedAuth } from '@/context/CentralizedAuthProvider';
import { useNavigate } from 'react-router-dom';

export const useBookings = () => {
  const [loading, setLoading] = useState(false);
  const { user } = useCentralizedAuth();
  const navigate = useNavigate();

  const applyCouponToBooking = async (
    originalCharge: number,
    couponCode: string
  ) => {
    if (!couponCode.trim()) {
      return { discount: 0, finalAmount: originalCharge };
    }

    try {
      const { data: couponData, error: couponError } = await supabase
        .from('coupons')
        .select('*')
        .eq('code', couponCode.trim())
        .eq('active', true)
        .eq('coupon_type', 'Booking')
        .maybeSingle();

      if (couponError || !couponData) {
        toast.error('Invalid or expired coupon code');
        return { discount: 0, finalAmount: originalCharge };
      }

      let discountAmount = 0;
      if (couponData.discount_type === 'percentage') {
        discountAmount = Math.round((originalCharge * couponData.discount_value) / 100);
      } else {
        discountAmount = couponData.discount_value;
      }

      const finalAmount = Math.max(originalCharge - discountAmount, 1);
      
      return { discount: discountAmount, finalAmount };
    } catch (error) {
      console.error('Error applying coupon:', error);
      toast.error('Failed to apply coupon');
      return { discount: 0, finalAmount: originalCharge };
    }
  };

  const createBooking = async (
    partnerId: string,
    associationId: string,
    pricingId: string,
    sessionTime: Date,
    sessionDuration: number,
    totalCharge: number,
    couponCode?: string,
    isFromSessionStorage: boolean = false
  ) => {
    if (!user) {
      toast.error('You must be logged in to book a session');
      return null;
    }

    try {
      setLoading(true);
      console.log('Creating booking with params:', {
        partnerId,
        associationId,
        pricingId,
        sessionTime: sessionTime.toISOString(),
        sessionDuration,
        totalCharge,
        isFromSessionStorage
      });

      const originalAmount = totalCharge;
      let discountAmount = 0;
      let finalPaidAmount = originalAmount;

      if (couponCode) {
        const { discount, finalAmount } = await applyCouponToBooking(originalAmount, couponCode);
        discountAmount = discount;
        finalPaidAmount = finalAmount;
      }

      if (!isFromSessionStorage) {
        const { data: pricingExists, error: pricingCheckError } = await supabase
          .from('partner_pricing')
          .select('id')
          .eq('id', pricingId)
          .single();
        
        if (pricingCheckError || !pricingExists) {
          console.error('Invalid pricing ID:', pricingId, pricingCheckError);
          toast.error('Invalid pricing information. Please try again.');
          return null;
        }
      }

      const newBooking: Omit<Booking, 'id' | 'created_at'> = {
        partner_id: partnerId,
        profile_id: user.id,
        pricing_id: pricingId,
        association_id: associationId,
        session_time: sessionTime.toISOString(),
        session_duration: sessionDuration,
        total_charge: finalPaidAmount,
        payment_status: 'pending',
        razorpay_booking_id: null,
        razorpay_payment_id: null,
        original_amount: originalAmount,
        coupon_code: couponCode || null,
        coupon_discount: discountAmount,
        paid_amount: finalPaidAmount
      };

      console.log('Submitting booking:', newBooking);

      const { data, error } = await supabase
        .from('bookings')
        .insert(newBooking)
        .select()
        .single();

      if (error) {
        console.error('Database error creating booking:', error);
        throw error;
      }

      toast.success('Booking created successfully');
      
      localStorage.setItem('current_booking_id', data.id);
      
      return data;
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error('Failed to create booking');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const verifyBookingPayment = async (paymentData: BookingPayment): Promise<BookingVerificationResponse> => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase.functions.invoke<BookingVerificationResponse>(
        'verify-booking-payment',
        {
          body: paymentData
        }
      );

      if (error) {
        console.error('Error verifying booking payment:', error);
        toast.error('Payment verification failed');
        return { 
          success: false, 
          error: error.message 
        };
      }

      return data || { success: false, error: 'No response from verification service' };
    } catch (error) {
      console.error('Error in booking payment verification:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred' 
      };
    } finally {
      setLoading(false);
    }
  };

  const getUserBookings = async () => {
    if (!user) return [];

    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('bookings')
        .select(`
          *,
          partners(*),
          pricing:partner_pricing(*),
          association:associations(*)
        `)
        .eq('profile_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      console.log('getUserBookings result:', data);
      return data;
    } catch (error) {
      console.error('Error fetching user bookings:', error);
      toast.error('Failed to load your bookings');
      return [];
    } finally {
      setLoading(false);
    }
  };

  return {
    createBooking,
    getUserBookings,
    applyCouponToBooking,
    verifyBookingPayment,
    loading
  };
};

export default useBookings;
